import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { consentService, ConsentType, ComplianceStatus } from '../services/consentService';
import { toast } from 'react-toastify';

const StudentConsentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [complianceStatus, setComplianceStatus] = useState<ComplianceStatus | null>(null);
  const [mandatoryConsents, setMandatoryConsents] = useState<ConsentType[]>([]);
  const [acceptedConsents, setAcceptedConsents] = useState<Set<number>>(new Set());
  const [activeStep, setActiveStep] = useState(0);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState<ConsentType | null>(null);

  useEffect(() => {
    checkCompliance();
  }, []);

  const checkCompliance = async () => {
    try {
      setLoading(true);
      const status = await consentService.checkComplianceStatus();
      setComplianceStatus(status);
      
      if (status.isCompliant) {
        // User has already given all mandatory consents
        navigate('/dashboard');
      } else {
        // Load mandatory consents
        const consents = status.mandatoryConsents || await consentService.getMandatoryConsentTypes();
        setMandatoryConsents(consents);
      }
    } catch (error) {
      console.error('Error checking compliance:', error);
      toast.error('Failed to load consent requirements');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptConsent = (consentId: number) => {
    setAcceptedConsents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(consentId)) {
        newSet.delete(consentId);
      } else {
        newSet.add(consentId);
      }
      return newSet;
    });
  };

  const handleViewConsent = (consent: ConsentType) => {
    setSelectedConsent(consent);
    setShowConsentDialog(true);
  };

  const handleNext = () => {
    if (activeStep < mandatoryConsents.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleSubmitConsents = async () => {
    try {
      setSubmitting(true);
      
      // Submit each accepted consent
      const consentArray = Array.from(acceptedConsents);
      for (const consentId of consentArray) {
        await consentService.giveConsent({ consentTypeId: consentId });
      }
      
      toast.success('Consents submitted successfully!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error submitting consents:', error);
      toast.error(error.message || 'Failed to submit consents');
    } finally {
      setSubmitting(false);
    }
  };

  const allConsentsAccepted = mandatoryConsents.every(consent => acceptedConsents.has(consent.id));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Welcome to Psychology Profile System
      </Typography>
      
      <Typography variant="h6" align="center" color="textSecondary" paragraph>
        Before you begin, please review and accept the following consent forms
      </Typography>

      <Box sx={{ mt: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {mandatoryConsents.map((consent, index) => (
            <Step key={consent.id}>
              <StepLabel>{consent.name}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ mt: 4 }}>
        {mandatoryConsents.map((consent, index) => (
          <Box
            key={consent.id}
            sx={{ display: activeStep === index ? 'block' : 'none' }}
          >
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {consent.name}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" paragraph>
                  {consent.description}
                </Typography>

                <Paper 
                  sx={{ 
                    p: 3, 
                    mt: 2, 
                    mb: 3, 
                    maxHeight: '400px', 
                    overflow: 'auto',
                    backgroundColor: 'grey.50'
                  }}
                >
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {consent.content}
                  </Typography>
                </Paper>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptedConsents.has(consent.id)}
                        onChange={() => handleAcceptConsent(consent.id)}
                        color="primary"
                      />
                    }
                    label="I have read and agree to the above terms"
                  />
                  
                  <Button
                    variant="text"
                    onClick={() => handleViewConsent(consent)}
                  >
                    View Full Text
                  </Button>
                </Box>

                {consent.requiresSignature && acceptedConsents.has(consent.id) && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This consent requires a signature. You will be prompted to sign electronically.
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Back
              </Button>
              
              {activeStep === mandatoryConsents.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmitConsents}
                  disabled={!allConsentsAccepted || submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit All Consents'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!acceptedConsents.has(consent.id)}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {mandatoryConsents.length === 0 && (
        <Alert severity="info">
          No consent forms are required at this time. You will be redirected to the dashboard.
        </Alert>
      )}

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          By using this system, you acknowledge that your data will be used to provide mental health support services.
        </Typography>
      </Box>

      {/* Consent Dialog */}
      <Dialog
        open={showConsentDialog}
        onClose={() => setShowConsentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedConsent?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            {selectedConsent?.description}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
            {selectedConsent?.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConsentDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StudentConsentPage;